"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronDown, Compass, Clock, House as Home, FileCheck2, Calculator, ArrowRight, Phone, Calendar, Layers, TrendingUp } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface FaqItem {
  id: string;
  category: string;
  iconName: "compass" | "clock" | "home" | "permit" | "calculator" | "layers";
  question: string;
  answer: string;
  takeaway: string;
}
const faqItems: FaqItem[] = [{
  id: "remodeling-scopes",
  category: "Services & Scope",
  iconName: "layers",
  question: "What whole-home and room-by-room scopes do you offer across Decatur?",
  answer: "We provide comprehensive residential remodeling spanning structural reconfiguration, second-story and ground-floor home additions, finished basement conversions, and full interior overhauls. For structural reconfigurations, we remove load-bearing partitions, engineer flush LVL/steel ceiling beams, and create open-concept kitchen-living layouts that respect architectural balance. Our home additions range from bump-out primary suites and scullery expansions to full second-story additions with roofline integration. Basement scopes transform underutilized daylight or walk-out basements into legal guest suites, home offices, and media rooms complete with subfloor waterproofing, egress windows, and quiet HVAC zoning. Throughout the interior, we replace outdated millwork, install bespoke cabinetry, level settling subfloors, and replace all rough infrastructure from plumbing supply lines to 200-amp electrical panels.",
  takeaway: "Full design-build coverage from structural framing and footprint additions to custom room-by-room finishes."
}, {
  id: "historic-bungalows-ranches",
  category: "Decatur Architecture",
  iconName: "home",
  question: "What unique structural challenges exist in older Decatur bungalows and ranches?",
  answer: "Decatur's housing stock features early 20th-century Craftsman bungalows in neighborhoods like Oakhurst, Winnona Park, and McDonough-Adams-Kings (MAK), alongside 1950s–1960s brick ranches in Great Lakes and Chelsea Heights. Craftsman bungalows frequently feature balloon framing without modern fire-blocking, settling pier-and-beam foundations, ungrounded knob-and-tube wiring, and aging cast-iron waste stacks that require complete modernization. Mid-century brick ranches typically present low 8-foot ceiling heights, segmented compartmentalized floor plans, undersized 100-amp electrical panels inadequate for modern heat pumps and induction ranges, and uninsulated crawlspaces. We conduct thorough pre-construction structural assessments and laser floor-leveling surveys to engineer hidden structural support, sister bowed joists, and safely modernize historic framing while preserving original trim details, heart pine flooring, and architectural integrity.",
  takeaway: "We proactively diagnose balloon framing, undersized electrical services, and foundation settling before construction begins."
}, {
  id: "permits-city-vs-dekalb",
  category: "Permitting & Zoning",
  iconName: "permit",
  question: "How do City of Decatur permits compare with unincorporated DeKalb County?",
  answer: "Permitting within the incorporated City of Decatur differs significantly from unincorporated DeKalb County. City of Decatur projects are governed by rigorous Unified Development Ordinance (UDO) zoning requirements, strict lot coverage and impervious surface ratios, and one of the state's most stringent Tree Canopy Conservation Ordinances. Any ground disturbance, foundation expansion, or scaffolding within critical root zones requires an upfront Arborist review and tree protection plan. Additionally, properties within designated historic districts—such as MAK, Clairemont, or Old Decatur—require Certificate of Appropriateness (COA) approval from the Decatur Historic Preservation Commission before exterior modifications, window replacements, or roofline changes can proceed. In unincorporated DeKalb County, tree and historic reviews are less restrictive, but county building department review times can take 6 to 12 weeks. We handle all architectural filings, engineering submittals, arborist consultations, historic board presentations, and field inspections in-house.",
  takeaway: "We manage the entire approval lifecycle across both City of Decatur UDO / historic regulations and DeKalb County jurisdictions."
}, {
  id: "project-timelines",
  category: "Schedule & Sequencing",
  iconName: "clock",
  question: "What factors commonly extend or shorten a whole-home remodeling timeline?",
  answer: "A complete whole-home renovation typically requires 4 to 8 months of active on-site construction, preceded by 6 to 10 weeks of pre-construction architectural design, structural engineering review, and municipal permit clearance. Timelines are shortened most effectively when clients vacate the home during construction—allowing unrestricted daily trade access and simultaneous multi-trade sequencing (such as rough electrical, plumbing, and mechanical rough-ins happening concurrently across multiple levels). Factors that extend schedules include phased in-place living (which requires progressive dust barriers, active temporary utilities, and sequential room turnovers), complex structural engineering revisions for multi-span steel beams, municipal plan review backlogs, and late changes to long-lead custom items like custom cabinetry, imported tile, or architectural windows.",
  takeaway: "Vacated homes and finalized early material orders yield the fastest, most cost-efficient project delivery."
}, {
  id: "scope-workflow",
  category: "Design-Build Process",
  iconName: "compass",
  question: "How does your integrated design-build process prevent scope creep and delays?",
  answer: "Under our unified design-build model, your lead architect, structural engineer, and project superintendent collaborate from initial schematic design through final punch-list. We establish 3D spatial models, exact finish schedules, and fixed material specifications before opening any wall. This integrated approach eliminates the communication friction common between independent architects and third-party contractors, ensuring your construction drawings match real-world Decatur building costs and trade labor availability from day one.",
  takeaway: "Single-point accountability eliminates contractor-architect disputes and protects your target completion date."
}, {
  id: "budget-contingencies",
  category: "Investment & Pricing",
  iconName: "calculator",
  question: "How do fixed-price modeling and contingency reserves work?",
  answer: "We provide itemized, fixed-price contracts supported by detailed trade scopes and confirmed supplier allowances. Because vintage Decatur homes frequently contain hidden conditions behind historic plaster or subflooring—such as discontinued plumbing runs or undersized joist spans—we recommend a dedicated 10% to 15% contingency reserve. This reserve remains under your strict authorization, ensuring that any unforeseen structural corrections are handled transparently without derailing your core renovation budget.",
  takeaway: "Transparent fixed-price scopes paired with a 10-15% contingency buffer keep your renovation secure and predictable."
}];
const costVsValueData = [{
  project: "Major kitchen remodel (midrange)",
  cost: "$79,591",
  resaleValue: "$41,805",
  recouped: "52.5%"
}, {
  project: "Bath remodel (upscale)",
  cost: "$79,717",
  resaleValue: "$36,128",
  recouped: "45.3%"
}, {
  project: "Basement remodel",
  cost: "$49,681",
  resaleValue: "$45,636",
  recouped: "91.9%"
}, {
  project: "Primary suite addition (midrange)",
  cost: "$162,109",
  resaleValue: "$59,369",
  recouped: "36.6%"
}];
export default function RemodelingFaq() {
  const [openId, setOpenId] = useState<string | null>("remodeling-scopes");
  const shouldReduceMotion = useReducedMotion();
  const toggleAccordion = (id: string) => {
    setOpenId(current => current === id ? null : id);
  };
  const getFaqIcon = (name: FaqItem["iconName"]) => {
    switch (name) {
      case "compass":
        return <Compass className="size-5 text-chart-1" />;
      case "clock":
        return <Clock className="size-5 text-chart-1" />;
      case "home":
        return <Home className="size-5 text-chart-1" />;
      case "permit":
        return <FileCheck2 className="size-5 text-chart-1" />;
      case "calculator":
        return <Calculator className="size-5 text-chart-1" />;
      case "layers":
        return <Layers className="size-5 text-chart-1" />;
      default:
        return <Compass className="size-5 text-chart-1" />;
    }
  };
  return <section id="whole-home-remodeling-faq" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 text-foreground transition-colors overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }}>
            <Badge variant="outline" className="border-border bg-secondary px-3 py-1 text-xs font-semibold uppercase tracking-widest text-secondary-foreground">
              Decatur Renovation Guide
            </Badge>

            <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl text-foreground">
              Frequently asked questions on whole-home remodeling
            </h2>

            <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
              Explore in-depth guidance on whole-home and room-by-room scopes, older Decatur home engineering, City of Decatur versus DeKalb County permitting nuances, trade sequencing, and architectural investment planning for historic and mid-century properties.
            </p>
          </motion.div>
        </div>

        {/* FAQ Accordion Stack */}
        <div className="mx-auto mt-12 max-w-3xl space-y-4">
          {faqItems.map((item, index) => {
          const isOpen = openId === item.id;
          return <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} whileInView={shouldReduceMotion ? undefined : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.06,
            ease: [0.16, 1, 0.3, 1]
          }} className="overflow-hidden rounded-lg border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-300 hover:border-chart-1/40 hover:shadow-md">
                <Button variant="ghost" type="button" onClick={() => toggleAccordion(item.id)} aria-expanded={isOpen} aria-controls={`faq-answer-${item.id}`} className="h-auto flex w-full items-center justify-between gap-4 p-5 sm:p-6 text-left font-normal whitespace-normal hover:bg-transparent transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
                  <div className="flex items-start gap-3.5 sm:gap-4">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-secondary border border-border">
                      {getFaqIcon(item.iconName)}
                    </div>
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {item.category}
                      </span>
                      <h3 className="font-sans text-base sm:text-lg font-semibold text-card-foreground">
                        {item.question}
                      </h3>
                    </div>
                  </div>

                  <div className={`flex size-8 shrink-0 items-center justify-center rounded-lg bg-secondary/80 text-muted-foreground transition-transform duration-350 ${isOpen ? "rotate-180 text-foreground bg-secondary" : ""}`}>
                    <ChevronDown className="size-4" />
                  </div>
                </Button>

                {isOpen && <div id={`faq-answer-${item.id}`} role="region" className="border-t border-border/60 px-5 pt-4 pb-6 sm:px-6 sm:pb-6 text-sm sm:text-base text-muted-foreground">
                    <p className="font-sans leading-relaxed text-foreground/90">
                      {item.answer}
                    </p>

                    <div className="mt-4 flex items-start gap-2.5 rounded-md bg-secondary/60 p-3 text-xs sm:text-sm text-secondary-foreground border border-border/50">
                      <span className="font-semibold text-foreground shrink-0">Key takeaway:</span>
                      <span>{item.takeaway}</span>
                    </div>
                  </div>}
              </motion.div>;
        })}
        </div>

        {/* Cost vs Value Table Card */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 16
      }} whileInView={shouldReduceMotion ? undefined : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto mt-12 max-w-3xl rounded-xl border border-border bg-card/85 p-6 sm:p-8 shadow-xs backdrop-blur-md transition-[border-color,box-shadow] duration-300 hover:border-chart-1/30 hover:shadow-md">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-4 border-b border-border/70">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-chart-1">
                <TrendingUp className="size-4" />
                <span>Market ROI Data</span>
              </div>
              <h3 className="mt-1 font-serif text-xl sm:text-2xl font-semibold text-card-foreground">
                Atlanta remodeling cost vs value (2025)
              </h3>
            </div>
          </div>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-left border-collapse font-sans text-sm">
              <caption className="sr-only">
                National survey averages for the Atlanta market, not quotes
              </caption>
              <thead>
                <tr className="border-b border-border bg-secondary/60 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <th scope="col" className="py-3 px-3.5 sm:px-4 rounded-l-md">
                    Project
                  </th>
                  <th scope="col" className="py-3 px-3.5 sm:px-4">
                    Average Cost
                  </th>
                  <th scope="col" className="py-3 px-3.5 sm:px-4">
                    Resale Value Added
                  </th>
                  <th scope="col" className="py-3 px-3.5 sm:px-4 text-right rounded-r-md">
                    % Cost Recouped
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60 text-card-foreground">
                {costVsValueData.map((row, index) => <tr key={row.project} data-index={index} className="hover:bg-secondary/40 transition-colors">
                    <td className="py-3.5 px-3.5 sm:px-4 font-medium text-foreground">
                      {row.project}
                    </td>
                    <td className="py-3.5 px-3.5 sm:px-4 text-muted-foreground">
                      {row.cost}
                    </td>
                    <td className="py-3.5 px-3.5 sm:px-4 text-muted-foreground">
                      {row.resaleValue}
                    </td>
                    <td className="py-3.5 px-3.5 sm:px-4 text-right font-semibold text-chart-1">
                      {row.recouped}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-xs italic text-muted-foreground font-sans">
            National survey averages for the Atlanta market, not quotes
          </p>
          <p className="mt-1 text-xs text-muted-foreground/80 font-sans">
            Source: Remodeling Magazine / JLC Cost vs Value Report 2025, Atlanta, GA metro.
          </p>
        </motion.div>

        {/* Bottom Consultation Banner */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 16
      }} whileInView={shouldReduceMotion ? undefined : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto mt-12 max-w-3xl rounded-xl border border-border bg-card p-6 sm:p-8 shadow-sm">
          <div className="flex flex-col items-center justify-between gap-6 sm:flex-row text-center sm:text-left">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-chart-1">
                <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                <span>Have a specific property question?</span>
              </div>
              <h4 className="mt-1 font-serif text-xl sm:text-2xl font-semibold text-card-foreground">
                Discuss your Decatur remodeling plans
              </h4>
              <p className="mt-1 text-sm text-muted-foreground font-sans">
                Schedule an initial site walkthrough with our lead craftsmen to review scope, zoning, and budgeting.
              </p>
            </div>

            <div className="flex shrink-0 flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
              <Button asChild className="w-full sm:w-auto font-sans font-semibold">
                <Link to="/ContactPage">
                  Book a consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <a href="tel:+14708479076" className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary transition-colors w-full sm:w-auto">
                <Phone className="size-4 mr-2 text-chart-1" />
                <span>(470) 847-9076</span>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}